/**
 * CUSTOM: ScrollFloat + ScrollFade — vanilla JS port of ReactBits ScrollFloat
 * Uses IntersectionObserver (no GSAP). Splits text nodes into character spans,
 * preserves <em>, <br>, and other inline tags. Triggered on scroll into view.
 *
 * Usage:
 *   data-scroll-float        → char-by-char float-up animation (headings)
 *   data-scroll-fade          → simple fade-up animation (labels, subtext)
 *   data-scroll-reveal        → word-by-word blur + translate reveal (CTA headings)
 */
(function () {
  'use strict';

  /* ── Tunables ── */
  var FLOAT_STAGGER  = 0.015;  /* seconds between chars */
  var FLOAT_DURATION = 0.55;   /* transition duration */
  var FLOAT_EASING   = 'cubic-bezier(0.22, 1, 0.36, 1)'; /* expo ease-out */

  var FADE_DURATION  = 0.6;
  var FADE_EASING    = 'cubic-bezier(0.22, 1, 0.36, 1)';

  var REVEAL_STAGGER  = 0.045;  /* seconds between words */
  var REVEAL_DURATION = 0.7;
  var REVEAL_EASING   = 'cubic-bezier(0.22, 1, 0.36, 1)';

  var THRESHOLD = 0.15; /* viewport fraction to trigger */

  /* ── Inject base CSS once ── */
  var style = document.createElement('style');
  style.textContent = [
    /* ScrollFloat chars — initial state */
    '.sf-char{display:inline-block;opacity:0;transform:translateY(110%) scaleY(2.2) scaleX(0.75);transform-origin:50% 0%;will-change:opacity,transform;}',
    /* ScrollFloat active — transition to final state */
    '[data-scroll-float].sf-active .sf-char{opacity:1;transform:translateY(0) scaleY(1) scaleX(1);transition:opacity ' + FLOAT_DURATION + 's ' + FLOAT_EASING + ',transform ' + FLOAT_DURATION + 's ' + FLOAT_EASING + ';}',
    /* Word wrapper — prevents line-break mid-word */
    '.sf-word{display:inline-block;white-space:nowrap;}',
    /* Overflow hidden on container to hide chars below baseline */
    '[data-scroll-float]{overflow:hidden;}',

    /* ScrollFade — initial state */
    '[data-scroll-fade]{opacity:0;transform:translateY(18px);transition:opacity ' + FADE_DURATION + 's ' + FADE_EASING + ',transform ' + FADE_DURATION + 's ' + FADE_EASING + ';}',
    '[data-scroll-fade].sf-active{opacity:1;transform:translateY(0);}',

    /* ScrollReveal words — initial state */
    '.sr-word{display:inline-block;opacity:0.08;filter:blur(5px);transform:translateY(22px);will-change:opacity,filter,transform;}',
    '[data-scroll-reveal].sf-active .sr-word{opacity:1;filter:blur(0px);transform:translateY(0);transition:opacity ' + REVEAL_DURATION + 's ' + REVEAL_EASING + ',filter ' + REVEAL_DURATION + 's ' + REVEAL_EASING + ',transform ' + REVEAL_DURATION + 's ' + REVEAL_EASING + ';}',
    /* Keep whitespace between words */
    '.sr-space{display:inline;white-space:pre;}'
  ].join('\n');
  document.head.appendChild(style);

  /* ── Character splitter — recursive, preserves <em> <strong> <br> etc ── */
  /* Groups characters by word so line-breaks only happen at spaces, not mid-word */
  function splitChars(el) {
    var charIndex = 0;

    function walk(node) {
      if (node.nodeType === 3) { /* TEXT_NODE */
        var text = node.textContent;
        if (!text) return;
        var frag = document.createDocumentFragment();
        /* Split into words and spaces to group chars per word */
        var parts = text.split(/( +)/);
        for (var p = 0; p < parts.length; p++) {
          var part = parts[p];
          if (!part) continue;
          if (/^ +$/.test(part)) {
            /* Space(s) between words — allow line-break here */
            for (var s = 0; s < part.length; s++) {
              var spaceSpan = document.createElement('span');
              spaceSpan.className = 'sf-char';
              spaceSpan.style.transitionDelay = (charIndex * FLOAT_STAGGER) + 's';
              spaceSpan.innerHTML = '\u00A0';
              frag.appendChild(spaceSpan);
              charIndex++;
            }
          } else {
            /* Word — wrap in a nowrap container so chars don't split across lines */
            var wordWrap = document.createElement('span');
            wordWrap.className = 'sf-word';
            for (var i = 0; i < part.length; i++) {
              var span = document.createElement('span');
              span.className = 'sf-char';
              span.style.transitionDelay = (charIndex * FLOAT_STAGGER) + 's';
              span.textContent = part[i];
              wordWrap.appendChild(span);
              charIndex++;
            }
            frag.appendChild(wordWrap);
          }
        }
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === 1) { /* ELEMENT_NODE */
        if (node.tagName === 'BR') return; /* leave <br> intact */
        /* Snapshot children since we mutate DOM */
        var kids = Array.prototype.slice.call(node.childNodes);
        for (var j = 0; j < kids.length; j++) {
          walk(kids[j]);
        }
      }
    }

    var kids = Array.prototype.slice.call(el.childNodes);
    for (var k = 0; k < kids.length; k++) {
      walk(kids[k]);
    }
    return charIndex;
  }

  /* ── Word splitter for ScrollReveal — preserves <em> <strong> etc ── */
  function splitWords(el) {
    var wordIndex = 0;

    function walk(node) {
      if (node.nodeType === 3) { /* TEXT_NODE */
        var text = node.textContent;
        if (!text) return;
        var frag = document.createDocumentFragment();
        /* Split on whitespace boundaries, keeping spaces as separators */
        var parts = text.split(/(\s+)/);
        for (var i = 0; i < parts.length; i++) {
          var part = parts[i];
          if (!part) continue;
          if (/^\s+$/.test(part)) {
            /* Whitespace — insert as a plain text node (no animation) */
            var sp = document.createElement('span');
            sp.className = 'sr-space';
            sp.textContent = part;
            frag.appendChild(sp);
          } else {
            var span = document.createElement('span');
            span.className = 'sr-word';
            span.style.transitionDelay = (wordIndex * REVEAL_STAGGER) + 's';
            span.textContent = part;
            frag.appendChild(span);
            wordIndex++;
          }
        }
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === 1) {
        if (node.tagName === 'BR') return;
        var kids = Array.prototype.slice.call(node.childNodes);
        for (var j = 0; j < kids.length; j++) {
          walk(kids[j]);
        }
      }
    }

    var kids = Array.prototype.slice.call(el.childNodes);
    for (var k = 0; k < kids.length; k++) {
      walk(kids[k]);
    }
    return wordIndex;
  }

  /* ── IntersectionObserver — triggers animation once ── */
  /* Only picks up elements not bound yet, so it can re-run safely after the
     Theme Editor re-renders a section (otherwise the new markup stays at opacity 0) */
  function unbound(selector) {
    var els = Array.prototype.slice.call(document.querySelectorAll(selector + ':not([data-sf-bound])'));
    els.forEach(function (el) { el.setAttribute('data-sf-bound', ''); });
    return els;
  }

  function init() {
    var floatEls   = unbound('[data-scroll-float]');
    var fadeEls    = unbound('[data-scroll-fade]');
    var revealEls  = unbound('[data-scroll-reveal]');

    /* Split text for float elements */
    floatEls.forEach(function (el) {
      splitChars(el);
    });

    /* Split text for reveal elements */
    revealEls.forEach(function (el) {
      splitWords(el);
    });

    /* Single observer for all animated elements */
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('sf-active');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: THRESHOLD });

    floatEls.forEach(function (el) { observer.observe(el); });
    fadeEls.forEach(function (el) { observer.observe(el); });
    revealEls.forEach(function (el) { observer.observe(el); });
  }

  /* ── Run on DOM ready ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
